<template>
  <v-card
    color="transparent"
    data-aos="fade"
    flat
  >
    <v-container>
      <v-row class="d-flex flex-wrap-reverse" justify="space-around">
        <v-col cols="12" sm="5" class="mb-5 order-xs-2 order-sm-1 text-center">
          <v-img :src="img" class="elevation-2" height="600px"></v-img>
          <v-container fluid style="padding:0" class="mb-2">
            <v-row no-gutters>
              <v-col
                class="pa-1 thumbnail"
                cols="4"
                v-for="thumbnail in thumbnails"
                :key="thumbnail.index"
              >
                <v-img
                  style="cursor: pointer"
                  :src="thumbnail.src"
                  height="150px"
                  @click="onClick(thumbnail)"
                ></v-img>
              </v-col>
            </v-row>
          </v-container>
          <v-btn
            large
            color="primary"
            depressed
            squared
            :href="formLink"
            target="_blank">BOOKING FORM
          </v-btn>
        </v-col>
        <v-col cols="12" sm="6" class="order-xs-1 order-sm-2">
          <h1 class="pt-2">Sourdough Masterclass</h1>
          <v-row class="pricing-details" justify="space-between">
            <v-col class="text-center">
              4 HOUR WORKSHOP
            </v-col>
          </v-row>
          <p >
            Sourdough baking is the most ancient way to make bread using flour, water and salt which are fermented
            using lactic acid bacteria and wild yeast. This process transforms the flour, giving depth of flavour and
            nutritional benefits.
          </p>

          <p>
            In this hands on workshop, we will teach you the techniques of making sourdough bread but most importantly
            help you build knowledge and confidence around it: more than a technique, sourdough is about understanding
            the process. We will also be discussing how and why consuming real sourdough contribute to our
            overall health.
          </p>

          <p>
            You will make and bake your own loaves and have plenty of time to ask your questions throughout. We will
            break to have a taste of homemade seasonal savoury and sweet sourdough bakes.
          </p>

          <p>
            This private workshop takes place in the comfort of your own home using your equipment. Knowing how to
            make our own sourdough bread is a real empowering experience!
          </p>

          <p>
            <b>Dates and Times</b> <br/>
            Availabilities throughout the week and weekend - please specify your preferred dates in the form.
          </p>
          <p>
            <b>Location</b> <br/>
            My kitchen in Hertford or yours.
          </p>
          <p>
            <b>Investment*</b> <br/>
            £225 for 2 persons from the same household. We offer discounts for a single person class,
            please <contact-link subject="Sourdough Masterclass"></contact-link>.<br/>
          </p>
          <p>
            If you would like to get a small group together, please enquire in the booking form.
            *Further travel charges may apply.
          </p>
          <p class="subtext">
            I realise a lot of us may currently be in a difficult financial
            position. If this is your case, please email us to discuss
            discounted rates. I strongly believe in the mental and physical
            benefits of making our own food and that everybody should
            have access to learning how to take care of oneself.
          </p>
        </v-col>
      </v-row>
      <v-row
        v-bind="binding"
        align="stretch"
        justify="center"
        class="fill-height my-5 you-will text-center">
        <v-col
          class="learn"
          :class="borderClass"
          cols="12"
          sm="4"
        >
          <h3>You will learn</h3>
          <p>
            <ul>
              <li>What is sourdough</li>
              <li>What is a starter, how to make it, store it and look after it</li>
              <li>Mixing, autolysis, bulk fermenting, stretch &amp; fold technique, shaping, scoring, baking</li>
              <li>How to achieve a lovely bake in a conventional oven</li>
              <li>Sourdough, nutrition and gut health</li>
              <li>Creative ways of using excess starter</li>
            </ul>
          </p>
        </v-col>
        <v-col
          class="make"
          :class="borderClass"
          cols="12"
          sm="4"
        >
          <h3>You will make*</h3>
          <v-row align="stretch" justify="center" class="fill-height">
            <v-row align="start" justify="center" no-gutters>
              <ul>
                <li>A rye sourdough loaf</li>
                <li>A country loaf</li>
                <li>Buckwheat galettes</li>
              </ul>
            </v-row>
            <v-row align="start" class="pa-3" justify="center" no-gutters>
              <p class="small-print">
                *May be subject to change. All the ingredients used are organically grown and
                local as much as possible.
              </p>
            </v-row>
          </v-row>
        </v-col>
        <v-col
          class="keep"
          :class="borderClass"
          cols="12"
          sm="4"
        >
          <h3>You will keep</h3>
          <p>
            <ul>
              <li>Your baked goods and a country loaf for you to bake the next morning</li>
              <li>An active organic rye starter</li>
              <li>A sourdough manual with everything you need to continue baking at home</li>
            </ul>
          </p>
        </v-col>
      </v-row>
    </v-container>
    <testimonials :filter="title"></testimonials>
  </v-card>
</template>

<script>
import {
  VBtn,
  VCard,
  VCol,
  VContainer,
  VImg,
  VRow,
} from 'vuetify/lib'
import ContactLink from '@/components/ContactLink'
import MetaInfoBuilder from '@/utils/social'
import Testimonials from '@/components/Testimonials'

export default {
  components: {
    ContactLink,
    Testimonials,
    VBtn,
    VCard,
    VCol,
    VContainer,
    VImg,
    VRow,
  },
  computed: {
    binding() {
      const binding = {}
      if (this.$vuetify.breakpoint.xsOnly) {
        binding.column = true
      }
      return binding
    },
    borderClass() {
      return this.$vuetify.breakpoint.xsOnly ?  'bottom-border' : 'right-border'
    },
    thumbnails() {
      return this.images.filter((image) => image.index != 0)
          .sort((a, b) => a.index - b.index)
    }
  },
  data: () => ({
    activeTab: null,
    images: [
      {
        index: 0,
        src: require('@/assets/images/introduction-to-sourdough.jpg?vuetify-preload'),
      },
      {
        index: 1,
        src: require('@/assets/images/sourdough-rye-bread.jpg?vuetify-preload'),
      },
      {
        index: 2,
        src: require('@/assets/images/white-sourdough-loaf.jpg?vuetify-preload'),
      },
      {
        index: 3,
        src: require('@/assets/images/sourdough.jpg?vuetify-preload'),
      }
    ],
    formLink: 'https://docs.google.com/forms/d/e/1FAIpQLSelhtrpL5rfnW5QrCAD6ue64od2T99devyfMLU7sjDG2x5i1g/viewform',
    img: require('@/assets/images/introduction-to-sourdough.jpg?vuetify-preload'),
    title: 'Cooking Classes',
  }),
  metaInfo() {
    return new MetaInfoBuilder()
      .description('Sourdough baking is the most ancient way to make bread using flour, water and salt which are '
        + 'fermented using lactic acid bacteria and wild yeast.')
      .image(`${window.location.origin}${this.img.src}`)
      .title('Sourdough Masterclass')
      .build()
  },
  methods: {
    onClick(thumbnail) {
      const current = this.images.find((image) => image.index == 0)

      current.index = thumbnail.index
      thumbnail.index = 0

      this.img = thumbnail.src
    }
  }

}
</script>

<style lang="scss" scoped>
h1 {
  color: #af2f27;
  font-family: 'Nanum Myeongjo', serif !important;
  font-size: 40px !important;
  text-transform: none;
  text-align: center;
}

.flyer {
  margin-bottom: 15px;
}

.underline {
  text-decoration: underline;
}

.terms p {
  font-size: 13px;
  font-style: italic;
}

.pricing-details {
  color: #697b5e;
  border-bottom: 1px solid #697b5e;
  margin-bottom: 23px;
  padding-bottom: 20px;
  text-transform: uppercase;
  font-weight: bold;
}

.v-image {
  margin-top: 14px;
}

strong {
  color: rgb(4,51,255);
  font-size: 25px;
}

.subtext {
  font-style: italic;
}

.you-will {

  .learn,
  .make,
  .keep {
    background: rgba($color: #697b5e, $alpha: 0.8);
    color: white;
    padding-top: 20px;
  }

  h3 {
    text-align: center;
    color: white;
  }

  ul {
    padding: 0;

    li {
      font-size: 15px;
      list-style-type: none;
      padding: 5px 0;
    }
  }

  p {
    padding: 0 20px;
    text-align: center;
  }

  .thumbnails {
    padding: 0 !important;

    .thumbnail {
      padding: 0 !important;
    }
  }

  .small-print {
    display: block;
    margin-top: 10px;
    font-style: italic;
    font-size: 13px;
  }

  .bottom-border {
    border-bottom: white 1px solid;
  }

  .right-border {
    border-right: white 1px solid;
  }
}

</style>
